import { useAuth } from '@/app/context';
import { useNavigation } from '@/shared/hooks';
import { useNavigate } from '@tanstack/react-router';
import { Page, PageHeader } from '@/shared/ui/page';
import { formatCurrency } from '@/shared/utils';
import { Button, TabsComponent } from '@noorden-insurtech/alba-design-system';
import { Home, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { CAPITAL_GLOBAL_PRODUCT_CODE } from '../../../app/config/products.config';
import { PremiumCard } from '../../proposals/components/proposal-steps/premium-card.component';
import { useProducts } from '../../quotes/hooks/common/use-products.hook';
import { CapitalGlobalPolicyTab } from './capital-global/capital-global-policy-tab.component';
import { CapitalGlobalBillingTab } from './capital-global/capital-global-billing-tab.component';
import { usePolicyDetails } from '../hooks/use-policy-details.hook';
import { useDocumentPdf } from '../hooks/use-document-pdf.hook';
import { usePolicyPdf } from '../hooks/use-policy-pdf.hook';
import { useStarterKit } from '../hooks/use-starter-kit.hook';
import { PolicyTab } from './policy-tab.component';
import { BillingTab } from './billing-tab.component';
import { DownloadPdfDialog } from './download-pdf.dialog';
import { SendPolicyEmailDialog } from './send-policy-email.dialog';
import { PolicyStatus } from '../../../shared/enums/policy-status.enum';
import { BrokerInactiveNotice } from '../../brokers/components/broker-inactive-notice.component';
import { useSelectedBrokerWriteGuard } from '../../brokers/hooks';

export type PolicyDetailsTab = 'policy' | 'billing';

type PolicyDetailsProps = {
  policyId: string;
  /**
   * Aba inicial, vinda do search param da rota. É o que permite à notificação
   * de emissão abrir direto na Cobrança, onde está o link de pagamento.
   */
  tab?: PolicyDetailsTab;
};

const policyStatusLabel: Record<number, string> = {
  [PolicyStatus.Effective]: 'Emitida',
  [PolicyStatus.Rejected]: 'Cancelada',
};

type DownloadDialogState = {
  open: boolean;
  title: string;
  message: string;
  action: (() => void) | null;
};

const initialDialogState: DownloadDialogState = {
  open: false,
  title: 'Baixar PDF',
  message: '',
  action: null,
};

export function PolicyDetails({ policyId, tab }: PolicyDetailsProps) {
  const [activeTab, setActiveTab] = useState<string>(tab ?? 'policy');
  const [dialog, setDialog] = useState<DownloadDialogState>(initialDialogState);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const { push } = useNavigation();
  const navigate = useNavigate();
  const { attributes } = useAuth();
  const { data: policy, isLoading, error } = usePolicyDetails(policyId);
  const { data: products } = useProducts();
  const policyPdf = usePolicyPdf();
  const starterKit = useStarterKit();
  const documentPdf = useDocumentPdf();
  // Corretora inativa: PDF, kit, e-mail e boletos da apólice travados (a Api
  // barra com 403). Em "Todos Corretores" não trava: a Api decide.
  const policyActionGuard = useSelectedBrokerWriteGuard('policy', 'other');

  // A URL manda: chegar na tela por um link com `?tab=billing` precisa abrir a
  // aba certa mesmo quando o componente já estava montado.
  useEffect(() => {
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const isPendingDownload = policyPdf.isPending || starterKit.isPending || documentPdf.isPending;
  const isErrorDownload = policyPdf.isError || starterKit.isError || documentPdf.isError;

  /**
   * A aba ativa também vive na URL, para que o link da notificação e o botão
   * voltar do navegador continuem apontando para a aba certa. `replace` evita
   * empilhar uma entrada de histórico a cada troca de aba.
   */
  function handleTabChange(nextTab: string) {
    setActiveTab(nextTab);
    void navigate({
      to: '/policies/$policyId',
      params: { policyId },
      search: { tab: nextTab as PolicyDetailsTab },
      replace: true,
    });
  }

  function openDownloadDialog(title: string, message: string, action: () => void) {
    policyPdf.reset();
    starterKit.reset();
    documentPdf.reset();
    setDialog({ open: true, title, message, action });
  }

  function closeDialog() {
    setDialog(initialDialogState);
  }

  function handleConfirmDownload() {
    dialog.action?.();
  }

  useEffect(() => {
    if ((policyPdf.isSuccess || starterKit.isSuccess || documentPdf.isSuccess) && dialog.open) {
      closeDialog();
      policyPdf.reset();
      starterKit.reset();
      documentPdf.reset();
    }
  }, [policyPdf.isSuccess, starterKit.isSuccess, documentPdf.isSuccess]);

  const totalPremium = policy?.grossPremium ?? policy?.duePremium ?? 0;
  const liquidPremium = policy?.rawPremium;
  const iofValue = policy?.iof ?? 0;

  const statusLabel =
    policyStatusLabel[policy?.policyStatus ?? 0] ?? 'Desconhecido';
  const policyNumber = policy?.externalPolicyNo || policy?.policyNo || '-';
  const productName =
    products?.find(p => p.code === policy?.productCode)?.name ??
    policy?.productCode ??
    '-';
  const proposalNumberDisplay = policy?.proposalNo || '-';
  const isCapitalGlobal = policy?.productCode === CAPITAL_GLOBAL_PRODUCT_CODE;

  if (isLoading) {
    return (
      <Page.Root>
        <div className='flex items-center justify-center py-20'>
          <Loader2 className='h-8 w-8 animate-spin text-alba-primary-500' />
        </div>
      </Page.Root>
    );
  }

  if (error || !policy) {
    return (
      <Page.Root>
        <div className='flex flex-col items-center justify-center py-20 gap-4'>
          <p className='text-alba-gray-600'>
            {error?.message || 'Erro ao carregar detalhes da apólice'}
          </p>
          <Button variant='outline' onClick={() => push('/policies')}>
            Voltar para apólices
          </Button>
        </div>
      </Page.Root>
    );
  }

  return (
    <Page.Root>
      <SendPolicyEmailDialog
        isOpen={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        policyId={policyId}
        brokerEmail={attributes?.email ?? ''}
        customerEmail={policy?.customer?.email ?? ''}
      />
      <DownloadPdfDialog
        isOpen={dialog.open}
        onClose={closeDialog}
        onConfirm={handleConfirmDownload}
        isLoading={isPendingDownload}
        isError={isErrorDownload}
        title={dialog.title}
        message={dialog.message}
      />
      <Page.Breadcrumb
        items={[
          {
            label: 'Pagina Inicial',
            href: '/dashboard',
            iconLeft: <Home className='h-4 w-4' />,
          },
          { label: 'Apólices', href: '/policies' },
          { label: `Apólice Nº ${policyNumber}`, active: true },
        ]}
      />

      <PageHeader.Root>
        <PageHeader.Main>
          <PageHeader.TitleGroup>
            <PageHeader.Title>
              <span className='block max-w-full whitespace-nowrap text-[1.7rem] leading-[1.15] sm:text-[3rem]'>
                Apólice Nº {policyNumber}
              </span>
            </PageHeader.Title>
            <PageHeader.Subtitle>
              Analise os dados e registros da inspeção para validação da proposta
            </PageHeader.Subtitle>
          </PageHeader.TitleGroup>
          <PageHeader.Actions className='grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:gap-3'>
            <Button
              size='sm'
              variant='outline'
              className='w-full'
              disabled={policyActionGuard.blocked}
              title={policyActionGuard.reason}
              onClick={() => setEmailDialogOpen(true)}
            >
              Enviar por E-mail
            </Button>
            <Button
              size='sm'
              className='w-full'
              disabled={policyActionGuard.blocked}
              title={policyActionGuard.reason}
              onClick={() =>
                openDownloadDialog(
                  'Baixar Kit da Apólice',
                  'O kit da apólice será baixado para o seu dispositivo.',
                  () => starterKit.mutate({ policyId, policyNo: policyNumber }),
                )
              }
            >
              Baixar Kit da Apólice
            </Button>
          </PageHeader.Actions>
        </PageHeader.Main>
      </PageHeader.Root>

      <BrokerInactiveNotice guards={[policyActionGuard]} />

      <TabsComponent.Root
        value={activeTab}
        onValueChange={(details) => handleTabChange(details.value)}
      >
        <TabsComponent.List className='grid grid-cols-2'>
          <TabsComponent.Trigger value='policy'>
            Apólice
          </TabsComponent.Trigger>
          <TabsComponent.Trigger value='billing'>
            Cobrança
          </TabsComponent.Trigger>
        </TabsComponent.List>

        <TabsComponent.Content value='policy'>
          {isCapitalGlobal ? (
            <div className='mt-6'>
              <CapitalGlobalPolicyTab
                data={policy}
                productName={productName}
                statusLabel={statusLabel}
                downloadGuard={policyActionGuard}
              />
            </div>
          ) : (
            <>
              <div className='mt-6'>
                <PremiumCard
                  totalValue={formatCurrency(totalPremium)}
                  liquidValue={liquidPremium != null ? formatCurrency(liquidPremium) : '-'}
                  interestPercentage=''
                  interestValue=''
                  iofValue={formatCurrency(iofValue)}
                  product={productName}
                  proposalNumber={proposalNumberDisplay}
                  proposalLabel='Número da Proposta'
                  status={statusLabel}
                  showPisCofins={false}
                />
              </div>
              <PolicyTab
                data={policy}
                downloadGuard={policyActionGuard}
                onDownload={(title, message, url, filename) =>
                  openDownloadDialog(title, message, () =>
                    documentPdf.mutate({ url, filename }),
                  )
                }
              />
            </>
          )}

          {/* Footer da aba Apólice */}
          <div className='flex flex-col gap-2 pt-4 pb-8 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:pt-10'>
            <button
              onClick={() => push('/policies')}
              className='order-2 self-center rounded-lg border border-alba-gray-200/70 px-4 py-2 text-sm font-bold text-alba-blue-500 hover:underline cursor-pointer bg-transparent sm:order-1 sm:self-start sm:border-0 sm:px-0 sm:py-0'
            >
              Sair
            </button>
            <div className='order-1 flex w-full flex-col gap-3 sm:order-2 sm:w-auto sm:flex-row'>
              <Button
                size='sm'
                variant='outline'
                className='w-full sm:w-auto'
                disabled={policyActionGuard.blocked}
                title={policyActionGuard.reason}
                onClick={() => setEmailDialogOpen(true)}
              >
                Enviar por E-mail
              </Button>
              <Button
                size='sm'
                className='w-full sm:w-auto'
                disabled={policyActionGuard.blocked}
                title={policyActionGuard.reason}
                onClick={() =>
                  openDownloadDialog(
                    'Baixar PDF',
                    'O PDF da apólice será baixado para o seu dispositivo.',
                    () => policyPdf.mutate({ policyId, policyNo: policyNumber }),
                  )
                }
              >
                Baixar PDF
              </Button>
              <Button
                size='sm'
                className='w-full sm:w-auto'
                disabled={policyActionGuard.blocked}
                title={policyActionGuard.reason}
                onClick={() =>
                  openDownloadDialog(
                    'Baixar Kit da Apólice',
                    'O kit da apólice será baixado para o seu dispositivo.',
                    () => starterKit.mutate({ policyId, policyNo: policyNumber }),
                  )
                }
              >
                Baixar Kit da Apólice
              </Button>
            </div>
          </div>
        </TabsComponent.Content>

        <TabsComponent.Content value='billing'>
          {isCapitalGlobal ? (
            <CapitalGlobalBillingTab
              data={policy}
              product={productName}
              proposalNumber={policyNumber}
              status={statusLabel}
              downloadGuard={policyActionGuard}
            />
          ) : (
            <BillingTab
              data={policy}
              product={productName}
              proposalNumber={proposalNumberDisplay}
              status={statusLabel}
              downloadGuard={policyActionGuard}
            />
          )}

          {/* Footer da aba Cobrança — só Sair */}
          <div className='flex justify-center pt-4 pb-8 sm:justify-start sm:pt-10'>
            <button
              onClick={() => push('/policies')}
              className='rounded-lg border border-alba-gray-200/70 px-4 py-2 text-sm font-bold text-alba-blue-500 hover:underline cursor-pointer bg-transparent sm:rounded-none sm:border-0 sm:px-0 sm:py-0'
            >
              Sair
            </button>
          </div>
        </TabsComponent.Content>
      </TabsComponent.Root>
    </Page.Root>
  );
}
